import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, type Observable } from 'rxjs';

import { type DancerRole } from '../../app/models/DancerRole';
import { type DancerModel } from '../../models/Dancer';
import { type LeaderboardItem } from '../../models/Leaderboard';
import { type ParticipantExtendedDto } from '../../models/Participant';
import { type PoolModel } from '../../models/Pool';
import { type ResponseWithData } from '../../models/ResponseWithData';
import { PoolStatus } from '../../models/Status';
import { type ContestRole, type UserFilters } from '../../models/User';
import { resolveHateoasUrl } from '../utils/hateoas.util';
import { buildHttpParams, type HttpOptions } from '../utils/http-params.util';
import { mapRawDancer, type RawParticipant } from '../utils/participant-mapper.util';

import { ApiService } from './api.service';

type ParticipantApiResponse = RawParticipant;

@Injectable({
  providedIn: 'root',
})
export class PoolApiService extends ApiService<PoolModel> {
  constructor() {
    super('pools');
  }

  updateStatus(
    id: string,
    status: PoolStatus,
    skipScoreCheck = false,
    skipStatusCheck = false,
  ): Observable<PoolModel> {
    let params = new HttpParams();
    if (status === PoolStatus.FINISHED && skipScoreCheck) {
      params = params.set('skipScoreCheck', 'true');
    }
    if (skipStatusCheck) {
      params = params.set('skipStatusCheck', 'true');
    }
    return this.http
      .patch<
        ResponseWithData<PoolModel>
      >(`${this.url}/${this.apiEndpoint}/${id}/status`, { status }, { params })
      .pipe(map((res) => res.data));
  }

  getParticipantsOfPool(
    poolOrId: PoolModel | string,
    options?: {
      filters?: {
        contestRole?: ContestRole;
        /** Repeat the param server-side (?dancerRole=A&dancerRole=B) to match any of them. */
        dancerRole?: DancerRole[];
      };
    },
  ): Observable<ParticipantExtendedDto[]> {
    const params = buildHttpParams(options as HttpOptions);

    const url = resolveHateoasUrl(poolOrId, `${this.url}/${this.apiEndpoint}`, 'participants');

    return this.http
      .get<ResponseWithData<ParticipantExtendedDto[]>>(url, { params })
      .pipe(map((res) => res.data));
  }

  getUserListOfPool(
    poolOrId: PoolModel | string,
    options?: {
      filters?: UserFilters;
    },
  ): Observable<DancerModel[]> {
    const params = buildHttpParams(options as HttpOptions);

    const url = resolveHateoasUrl(poolOrId, `${this.url}/${this.apiEndpoint}`, 'participants');

    return this.http
      .get<ResponseWithData<ParticipantApiResponse[]>>(url, {
        params,
      })
      .pipe(map((res) => res.data.map((p) => mapRawDancer(p))));
  }

  addParticipantToPool(poolId: string, participantId: string): Observable<void> {
    return this.http.post<void>(
      `${this.url}/${this.apiEndpoint}/${poolId}/participants/${participantId}`,
      {},
    );
  }

  getPoolLeaderboard(poolId: string): Observable<LeaderboardItem[]> {
    return this.http
      .get<
        ResponseWithData<LeaderboardItem[]>
      >(`${this.url}/${this.apiEndpoint}/${poolId}/leaderboard`)
      .pipe(map((res) => res.data));
  }

  addParticipantsToPool(
    poolId: string,
    participantIds: string[],
    skipStatusCheck = false,
  ): Observable<void> {
    let params = new HttpParams();
    if (skipStatusCheck) {
      params = params.set('skipStatusCheck', 'true');
    }
    return this.http.post<void>(
      `${this.url}/${this.apiEndpoint}/${poolId}/participants`,
      {
        participantIds,
      },
      { params },
    );
  }

  removeParticipantFromPool(poolId: string, participantId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.url}/${this.apiEndpoint}/${poolId}/participants/${participantId}`,
    );
  }

  removeParticipantsFromPool(poolId: string, participantIds: string[]): Observable<void> {
    return this.http.delete<void>(`${this.url}/${this.apiEndpoint}/${poolId}/participants`, {
      body: { participantIds },
    });
  }
}
